import React, { useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { fmtDate } from "../../../../lib/misc";
import { T } from "../../constants";
import { useUi, cx } from "../../ui";
import Panel from "../../components/Panel";
import Plate from "../../components/Plate";
import StatusBadge from "./StatusBadge";
import BookingRowAction, { statusNote } from "./BookingRowAction";

/* ---------- Pre-booked parking table ---------- */

const useStyles = makeStyles({
  wrap: {
    overflow: "auto",
    maxHeight: 520,
    padding: "8px 8px 4px",
  },
  table: { width: "100%", minWidth: 980, borderCollapse: "collapse", tableLayout: "fixed" },
  th: {
    textAlign: "left",
    fontSize: 12,
    fontWeight: 600,
    color: T.mutedFg,
    padding: "5px 10px",
    borderBottom: `0.5px solid ${T.borderSoft}`,
    letterSpacing: "0.04em",
    whiteSpace: "nowrap",
  },
  thRight: { textAlign: "right" },
  row: { borderBottom: `0.5px solid ${T.borderSoft}`, cursor: "pointer" },
  td: { padding: "8px 10px", fontSize: 13, fontWeight: 500, color: T.fg, verticalAlign: "middle" },
  tdRight: { textAlign: "right" },
  mono: { fontFamily: "monospace" },
  guest: { fontWeight: 600, color: T.fg },
  cardId: { fontWeight: 600, color: T.fg, fontFamily: "monospace" },
  cardLot: { fontSize: 12, color: T.mutedFg },
  italicMuted: { fontStyle: "italic", color: T.subtleFg, fontSize: 12 },
  statusCell: { display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 4 },
  statusNote: { fontSize: 10, textTransform: "uppercase", letterSpacing: 0.5, color: T.mutedFg },
  empty: { padding: "32px 10px", textAlign: "center", fontSize: 13, color: T.mutedFg },
  showMoreWrap: { display: "flex", justifyContent: "center", marginTop: 12, marginBottom: 16 },
});

const VISIBLE_BOOKINGS_LIMIT = 5;

export default function BookingsTable({ bookings, timezone, onOpenBooking, onTakePayment, onAssign, onValidate }) {
  const s = useStyles();
  const ui = useUi();
  const [showAllBookings, setShowAllBookings] = useState(false);
  const visibleBookings = showAllBookings ? bookings : bookings.slice(0, VISIBLE_BOOKINGS_LIMIT);
  const shouldShowBookingsToggle = bookings.length > VISIBLE_BOOKINGS_LIMIT;

  return (
    <>
      <Panel>
      <div className={s.wrap}>
        <table className={s.table}>
          <thead>
            <tr>
              <th className={s.th}>Licence Plate</th>
              <th className={s.th}>Guest</th>
              <th className={s.th}>Start Day</th>
              <th className={s.th}>End Day</th>
              <th className={s.th}>Guest Card</th>
              <th className={s.th}>Status</th>
              <th className={cx(s.th, s.thRight)}>Action</th>
            </tr>
          </thead>
          <tbody>
            {bookings.length === 0 ? (
              <tr>
                <td className={s.empty} colSpan={7}>
                  No bookings match those filters.
                </td>
              </tr>
            ) : (
              visibleBookings.map((b) => (
                <tr key={b.ref} className={s.row} onClick={() => onOpenBooking(b)}>
                  <td className={s.td}>
                    {b.plate ? <Plate size={12}>{b.plate}</Plate> : <span className={s.italicMuted}>to follow</span>}
                  </td>
                  <td className={s.td}>
                    <div className={s.guest}>{b.guestName}</div>
                  </td>
                  <td className={s.td}>{fmtDate(b.startDay, timezone, "ccc d LLL")}</td>
                  <td className={s.td}>{fmtDate(b.endDay, timezone, "ccc d LLL")}</td>
                  <td className={s.td}>
                    {b.voucherId ? (
                      <>
                        <div className={s.cardId}>{b.voucherId}</div>
                        <div className={s.cardLot}>{b.lotName}</div>
                      </>
                    ) : (
                      <span className={s.italicMuted}>Not allocated</span>
                    )}
                  </td>
                  <td className={s.td}>
                    <div className={s.statusCell}>
                      <StatusBadge status={b.status} />
                      {statusNote(b) && <span className={s.statusNote}>{statusNote(b)}</span>}
                    </div>
                  </td>
                  <td className={cx(s.td, s.tdRight)}>
                    <BookingRowAction
                      booking={b}
                      onTakePayment={onTakePayment}
                      onAssign={onAssign}
                      onValidate={onValidate}
                    />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      </Panel>
      {shouldShowBookingsToggle ? (
        <div className={s.showMoreWrap}>
          <button
            type="button"
            className={ui.ghostBtn}
            onClick={() => setShowAllBookings((value) => !value)}
          >
            {showAllBookings ? "Show less" : "Show more"}
          </button>
        </div>
      ) : null}
    </>
  );
}
